import { useState } from "react";
import { toast } from "react-toastify";
import { BounceLoader } from "react-spinners";
import { CalendarIcon, PostpaidIcon } from "../../../../assets/icons";
import { TRANSACTION_STATUS } from "../../../../model/transaction";
import { formatDate, formatTimeLocal } from "../../../../utils";
import TabSelector from "../../tab-selector/TabSelector";
import Button from "../button/Button";
import styles from "./TransactionDetail.module.scss";
import { useLazyDownloadTransactionInvoiceQuery } from "../../../../service/transaction-service";
import { getErrorMessage } from "../../../../utils/error-handler";
import TransactionOverview from "./TransactionOverview";
import TransactionTimeline from "./TransactionTimeline";
import { TimelineEntry } from "./timeline";
import { TransactionDetailData, TransactionDetailType } from "./types";

interface TransactionDetailsProps {
	transaction: TransactionDetailData;
	timeline?: TimelineEntry[];
	transactionType: TransactionDetailType;
	onClose: () => void;
	onRevend?: () => void;
	onNotifyBilling?: () => void;
	onVerifyVendingToken?: () => void;
	onVerifyPaymentStatus?: () => void;
	reVendLoading?: boolean;
	notifyBillingLoading?: boolean;
	verifyingVendingToken?: boolean;
	verifyingPayment?: boolean;
	isLoadingTransaction?: boolean;
	isError?: boolean;
}

const TransactionDetails = ({
	transaction,
	timeline,
	reVendLoading,
	notifyBillingLoading,
	verifyingVendingToken,
	verifyingPayment,
	transactionType,
	onClose,
	onRevend,
	onNotifyBilling,
	onVerifyVendingToken,
	onVerifyPaymentStatus,
	isLoadingTransaction = false,
	isError = false,
}: TransactionDetailsProps) => {
	const [activeTab, setActiveTab] = useState(0);
	const [downloadInvoice, { isLoading }] = useLazyDownloadTransactionInvoiceQuery();

	const showTabs = (timeline?.length ?? 0) > 0;

	const handleOnDownload = () => {
		downloadInvoice(transaction.reference)
			.unwrap()
			.then(res => {
				if (res) {
					window.open(res, "_blank");
				}
			})
			.catch(err => {
				toast.error(getErrorMessage(err?.data));
			});
	};

	return (
		<div className={styles.transactionDetails}>
			{isLoadingTransaction || isError ? (
				<></>
			) : (
				<div className={styles.header}>
					<div className={styles.dateTimeContainer}>
						<div className={styles.dateTime}>
							<div className={styles.dateItem}>
								<span className={styles.icon}>
									<CalendarIcon height={14} />
								</span>
								<span>{formatDate(transaction.date)}</span>
							</div>
							<span className={styles.color}>|</span>
							<div className={styles.dateItem}>
								<span className={styles.icon}>
									<PostpaidIcon height={14} />
								</span>
								<span>{formatTimeLocal(transaction.time)}</span>
							</div>
						</div>
					</div>
					<div className={styles.customerName}>{transaction.customerName}</div>
					<div className={styles.amount}>{transaction.amount}</div>
				</div>
			)}

			<div className={styles.bodyContent}>
				{showTabs && !isLoadingTransaction && !isError && (
					<div className={styles.tabsWrapper}>
						<TabSelector
							selectedTab={activeTab}
							tabs={[
								{ id: 0, title: "Transaction Overview", onClick: () => setActiveTab(0) },
								{ id: 1, title: "Timeline", onClick: () => setActiveTab(1) },
							]}
						/>
					</div>
				)}
				{isLoadingTransaction && (
					<div className={styles.loadingState}>
						<BounceLoader color="#DE841B" size={40} />
						<p>Loading transaction details...</p>
					</div>
				)}
				{isError && !isLoadingTransaction && (
					<div className={styles.errorState}>
						<p>Unable to load transaction details. Please try again.</p>
					</div>
				)}
				{!isLoadingTransaction && !isError && activeTab === 0 && (
					<TransactionOverview
						transaction={transaction}
						transactionType={transactionType}
						onRevend={onRevend}
						onNotifyBilling={onNotifyBilling}
						onVerifyVendingToken={onVerifyVendingToken}
						onVerifyPaymentStatus={onVerifyPaymentStatus}
						reVendLoading={reVendLoading}
						notifyBillingLoading={notifyBillingLoading}
						verifyingVendingToken={verifyingVendingToken}
						verifyingPayment={verifyingPayment}
					/>
				)}
				{!isLoadingTransaction && !isError && activeTab === 1 && <TransactionTimeline entries={timeline} />}
			</div>
			<div className={styles.footer}>
				<Button type="button" variant="outline" text="Close" onClick={onClose} />
				{transaction.paymentStatus === TRANSACTION_STATUS.COMPLETED && (
					<Button disabled={isLoading} type="button" variant="outline" text={isLoading ? `Downloading..` : "Download"} onClick={handleOnDownload} />
				)}
			</div>
		</div>
	);
};

export default TransactionDetails;
